import React from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';

import FilterTag from 'components/Filters/components/FilterTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { VehicleResultsFilters } from 'models/auto/vehicleResults';
import { formatNumberToCompact } from 'utils/generic';

import { Container, Icon, Label } from './styles';

interface Props {
  onChange: (filters: VehicleResultsFilters) => void;
}

const FilterTags = ({ onChange }: Props) => {
  const { t } = useStoreTranslation();
  const filters = useSelector((state: RootState) => state.autos.filters);

  if (filters)
    return (
      <Container>
        {filters.priceFrom && filters.priceTo && (
          <FilterTag
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
              };
              delete newFilters.priceFrom;
              delete newFilters.priceTo;
              onChange(newFilters);
            }}
          >
            {t('autosList.filterTags.priceRange', {
              from: formatNumberToCompact(t, filters.priceFrom),
              to: formatNumberToCompact(t, filters.priceTo),
            })}
          </FilterTag>
        )}

        {filters.vehicleCategory?.map((category) => (
          <FilterTag
            key={category}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                vehicleCategory: filters.vehicleCategory?.filter((a) => a !== category),
              };
              if (newFilters.vehicleCategory?.length === 0) delete newFilters.vehicleCategory;
              onChange(newFilters);
            }}
          >
            {category}
          </FilterTag>
        ))}

        {filters.transmissionType?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                transmissionType: filters.transmissionType?.filter((a) => a !== item),
              };
              if (newFilters.transmissionType?.length === 0) delete newFilters.transmissionType;
              onChange(newFilters);
            }}
          >
            <Label>
              <Icon.Transmission />
              {t(`autosCard.category.transmission.${item.toUpperCase()}`)}
            </Label>
          </FilterTag>
        ))}

        {filters.maxPax?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                maxPax: filters.maxPax?.filter((a) => a !== item),
              };
              if (newFilters.maxPax?.length === 0) delete newFilters.maxPax;
              onChange(newFilters);
            }}
          >
            <Label>
              <Icon.MaxPax />
              {item}
            </Label>
          </FilterTag>
        ))}

        {filters.doors?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                doors: filters.doors?.filter((a) => a !== item),
              };
              if (newFilters.doors?.length === 0) delete newFilters.doors;
              onChange(newFilters);
            }}
          >
            <Label>
              <Icon.Doors />
              {item}
            </Label>
          </FilterTag>
        ))}

        {filters.fuelType?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                fuelType: filters.fuelType?.filter((a) => a !== item),
              };
              if (newFilters.fuelType?.length === 0) delete newFilters.fuelType;
              onChange(newFilters);
            }}
          >
            {item === 'Unspecified' ? t('autosDetails.fuelType.unspecified') : `${item}`}
          </FilterTag>
        ))}

        {filters.airConditioned?.map((item, idx) => (
          <FilterTag
            key={idx}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                airConditioned: filters.airConditioned?.filter((a) => a !== item),
              };
              if (newFilters.airConditioned?.length === 0) delete newFilters.airConditioned;
              onChange(newFilters);
            }}
          >
            <Label>
              <Icon.AirConditioned />
              {t(`autosDetails.airConditioned.${String(item).toLowerCase()}`)}
            </Label>
          </FilterTag>
        ))}

        {filters.company?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                company: filters.company?.filter((a) => a !== item),
              };
              if (newFilters.company?.length === 0) delete newFilters.company;
              onChange(newFilters);
            }}
          >
            {item}
          </FilterTag>
        ))}

        {filters.model?.map((item) => (
          <FilterTag
            key={item}
            onDelete={() => {
              const newFilters: VehicleResultsFilters = {
                ...filters,
                model: filters.model?.filter((a) => a !== item),
              };
              if (newFilters.model?.length === 0) delete newFilters.model;
              onChange(newFilters);
            }}
          >
            {item}
          </FilterTag>
        ))}
      </Container>
    );

  return null;
};

export default FilterTags;
